/**
 * rubricaGuardada — tener la firma a mano para la próxima ronda.
 *
 * Firmar es raro de uno en uno y frecuente a lo largo del año: la misma gente
 * firma el acta de la misma reunión cada mes. Volver a dibujar el garabato cada
 * vez es lo que hace que las firmas del mes tres no se parezcan a las del mes
 * uno, y que al final alguien mande un «xx» por no pelearse con el ratón.
 *
 * ── Por qué en el navegador y no en la base de datos ────────────────────
 *
 * Porque guardarla en el servidor convierte un dibujo en una firma reutilizable
 * por el sistema: cualquier fallo que deje entrar a alguien en una cuenta
 * pasaría a dejarle estampar una firma ya hecha, sin que la persona toque
 * nunca el lienzo. Aquí la firma no sale del aparato de quien firma, y en cada
 * ronda hay que **traerla a mano, verla y aprobarla**. Es una comodidad, no un
 * poder.
 *
 * Y por eso va atada al correo: en un ordenador compartido —una recepción, una
 * sala— la de una persona no se le ofrece a la siguiente.
 *
 * ── Por qué todo va envuelto en try/catch ───────────────────────────────
 *
 * Porque `localStorage` no es una variable: en una ventana privada, con las
 * cookies bloqueadas o con el almacén lleno, **leerlo lanza**. Y lo que hay
 * detrás de esto es la pantalla de firmar, a la que llega alguien de fuera
 * desde un correo. Que no pueda guardar su firma es un inconveniente; que la
 * pantalla se quede en blanco por eso sería perder la ronda.
 */

/** El prefijo, para no pisar nada más de la aplicación. */
const CLAVE = 'minutasia.rubrica.';

/**
 * Lo más que se guarda. Un PNG de firma ronda los 10 KB; 400 deja sitio de
 * sobra y evita que una foto grande llene el almacén y rompa lo demás que
 * guarda la aplicación —que es lo que pasa cuando se guarda sin mirar—.
 */
export const TOPE = 400 * 1024;

function almacenPorDefecto() {
  try { return window.localStorage; } catch { return null; }
}

function claveDe(correo) {
  const limpio = String(correo || '').trim().toLowerCase();
  return limpio ? CLAVE + limpio : null;
}

/** Si una rúbrica es guardable. Sirve para no ofrecer lo que luego fallaría. */
export function cabe(base64) {
  return Boolean(base64) && String(base64).length <= TOPE;
}

/**
 * Guarda la rúbrica de este correo en este aparato.
 * @returns {boolean} si de verdad quedó guardada.
 */
export function guardar(correo, base64, almacen = almacenPorDefecto()) {
  const clave = claveDe(correo);
  if (!clave || !almacen || !cabe(base64)) return false;
  try {
    almacen.setItem(clave, JSON.stringify({ firma: base64, cuando: Date.now() }));
    return true;
  } catch {
    // Almacén lleno o escritura prohibida. No se avisa con un error: la firma
    // de esta ronda ya está hecha y se va a mandar igual.
    return false;
  }
}

/** La rúbrica guardada de este correo, o null. Nunca lanza. */
export function leer(correo, almacen = almacenPorDefecto()) {
  const clave = claveDe(correo);
  if (!clave || !almacen) return null;
  try {
    const crudo = almacen.getItem(clave);
    if (!crudo) return null;
    const guardado = JSON.parse(crudo);
    // Se comprueba la forma y no solo que haya algo: lo que hay en el almacén
    // lo pudo escribir una versión anterior de esta pantalla, y un objeto con
    // otra forma reventaría al usarlo, no al leerlo.
    if (!guardado || typeof guardado.firma !== 'string' || !guardado.firma) return null;
    return { firma: guardado.firma, cuando: Number(guardado.cuando) || null };
  } catch {
    return null;
  }
}

/** La borra de este aparato. */
export function olvidar(correo, almacen = almacenPorDefecto()) {
  const clave = claveDe(correo);
  if (!clave || !almacen) return false;
  try { almacen.removeItem(clave); return true; } catch { return false; }
}

/** «hace dos semanas», para que se sepa qué es lo que se va a reutilizar. */
export function cuandoSeGuardo(cuando) {
  if (!cuando) return '';
  const dias = Math.floor((Date.now() - cuando) / 86400000);
  if (dias <= 0) return 'guardada hoy';
  if (dias === 1) return 'guardada ayer';
  if (dias < 30) return `guardada hace ${dias} días`;
  const meses = Math.round(dias / 30);
  return meses === 1 ? 'guardada hace un mes' : `guardada hace ${meses} meses`;
}
